import {
  UpsertEmailIntegrationImapRequest,
  UpsertEmailIntegrationRequest,
  UpsertMetaWhatsappIntegrationRequest,
  UpsertTwilioIntegrationRequest,
} from "@/types/communication-integration";
import {
  EmailIntegrationFormData,
  TwilioIntegrationFormData,
  WhatsappIntegrationFormData,
} from "./integration-schemas";

const resolveSecret = (
  value: string,
  clear: boolean,
): string | null | undefined => {
  if (clear) return null;
  if (value.length > 0) return value;
  return undefined;
};

export const buildEmailIntegrationPayload = (
  data: EmailIntegrationFormData,
): UpsertEmailIntegrationRequest => {
  const smtpPassword = resolveSecret(
    data.smtp.password,
    data.smtp.clearPassword,
  );

  const smtp: UpsertEmailIntegrationRequest["smtp"] = {
    host: data.smtp.host.trim(),
    port: Number(data.smtp.port),
    secure: data.smtp.secure,
    username: data.smtp.username.trim(),
    fromAddress: data.smtp.fromAddress.trim(),
    fromName: data.smtp.fromName.trim(),
    replyToAddress: data.smtp.replyToAddress.trim(),
  };
  if (smtpPassword !== undefined) {
    smtp.password = smtpPassword;
  }

  let imap: UpsertEmailIntegrationImapRequest | null | undefined;
  if (!data.imap.enabled) {
    imap = null;
  } else {
    const imapPassword = resolveSecret(
      data.imap.password,
      data.imap.clearPassword,
    );
    const imapBlock: UpsertEmailIntegrationImapRequest = {
      host: data.imap.host.trim(),
      port: Number(data.imap.port),
      secure: data.imap.secure,
      username: data.imap.username.trim(),
      inboxFolder: data.imap.inboxFolder.trim() || "INBOX",
    };
    if (imapPassword !== undefined) {
      imapBlock.password = imapPassword;
    }
    imap = imapBlock;
  }

  return {
    enabled: data.enabled,
    smtp,
    imap,
  };
};

export const buildTwilioIntegrationPayload = (
  data: TwilioIntegrationFormData,
): UpsertTwilioIntegrationRequest => {
  const authToken = resolveSecret(data.authToken, data.clearAuthToken);
  const trimmedPhone = data.smsPhoneNumber.trim();

  let smsPhoneNumber: string | null;
  if (data.clearSmsPhoneNumber) {
    smsPhoneNumber = null;
  } else if (trimmedPhone.length > 0) {
    smsPhoneNumber = trimmedPhone;
  } else {
    smsPhoneNumber = null;
  }

  const payload: UpsertTwilioIntegrationRequest = {
    enabled: data.enabled,
    accountSid: data.accountSid.trim(),
    smsPhoneNumber,
  };
  if (authToken !== undefined) {
    payload.authToken = authToken;
  }
  return payload;
};

export const buildWhatsappIntegrationPayload = (
  data: WhatsappIntegrationFormData,
): UpsertMetaWhatsappIntegrationRequest => {
  const accessToken = resolveSecret(data.accessToken, data.clearAccessToken);
  const appSecret = resolveSecret(data.appSecret, data.clearAppSecret);
  const webhookVerifyToken = resolveSecret(
    data.webhookVerifyToken,
    data.clearWebhookVerifyToken,
  );

  const payload: UpsertMetaWhatsappIntegrationRequest = {
    enabled: data.enabled,
    phoneNumberId: data.phoneNumberId.trim(),
    wabaId: data.wabaId.trim(),
    displayPhoneNumber: data.displayPhoneNumber.trim(),
    graphApiVersion: data.graphApiVersion.trim() || "v22.0",
  };
  if (accessToken !== undefined) {
    payload.accessToken = accessToken;
  }
  if (appSecret !== undefined) {
    payload.appSecret = appSecret;
  }
  if (webhookVerifyToken !== undefined) {
    payload.webhookVerifyToken = webhookVerifyToken;
  }
  const phoneRegistrationPin = data.phoneRegistrationPin.trim();
  if (phoneRegistrationPin.length > 0) {
    payload.phoneRegistrationPin = phoneRegistrationPin;
  }
  return payload;
};
